import { inject, Injectable } from '@angular/core';

import { FormlyFieldConfig } from '@ngx-formly/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { map, Observable, of, take } from 'rxjs';

import { RECAPTCHA_V2_SITE_KEY } from '@valk-nx/core/lib/core';
import { RecaptchaRequest } from '@valk-nx/core/lib/interfaces/recaptcha.interface';

const DEFAULT_FIELD_NAME = 'token';

@Injectable({ providedIn: 'root' })
export class ReCaptchaService {
	private readonly recaptchaV3Service = inject(ReCaptchaV3Service);
	private readonly recaptchaV2Key = inject(RECAPTCHA_V2_SITE_KEY);

	addRecaptchaToken<T>(
		body: Partial<T>,
		recaptchaV3OperationId: string,
		propertyName: string = DEFAULT_FIELD_NAME,
	): Observable<T & RecaptchaRequest> {
		// Token is already in the body, which means it was read from a form using v2
		if (Object.hasOwn(body, propertyName) && !!body[propertyName]) {
			return of({ ...body, recaptchaVersion: 'v2' } as T &
				RecaptchaRequest);
		} else {
			// Invoke v3 to get the short-lived token
			return this.recaptchaV3Service.execute(recaptchaV3OperationId).pipe(
				take(1),
				map((recaptchaToken: string) => {
					body[propertyName] = recaptchaToken;
					body['recaptchaVersion'] = 'v3';
					return body as T & RecaptchaRequest;
				}),
			);
		}
	}

	addV2RecaptchaFormField(
		fallbackToV2Trigger: Observable<unknown>,
		formControlName: string = DEFAULT_FIELD_NAME,
		config = {
			required: false,
			errorMode: 'handled',
			size: 'default',
		},
	): FormlyFieldConfig {
		return {
			id: formControlName,
			key: formControlName,
			props: {
				...config,
				fallbackToV2Trigger,
				siteKey: this.recaptchaV2Key,
			},
			type: 'recaptchaV2Fallback',
			wrappers: ['label'],
		};
	}
}
